import React, { forwardRef, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import debounce from '../../../helpers/debounce';

import { useWindowSize } from '../../../hooks/useWindowSize';

import { containerClassName, pageHorizontalPaddings } from '../PlaybookTabsView/PlaybookTabsView';

import ArrowLeftSLineIcon from '../../../design-system/Icons/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from '../../../design-system/Icons/ArrowRightSLineIcon';

const buttonClassName = 'py-3 px-5 border-1 border-neutral-400';
const buttonWidth = 60;

const TabNavigation = ({ tabs, activeTab, setActiveTab }) => {
    const [visibleTabsCount, setVisibleTabsCount] = useState(1);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);

    const tabsContainerRef = useRef(null);
    const tabRefs = useRef([]);

    const [widths, setWidths] = useState(null);

    const withPrevButton = visibleStartIndex > 0;
    const withNextButton = visibleStartIndex + visibleTabsCount < tabs.length;

    const { width: screenWidth } = useWindowSize();

    const calculateCountOfVisibleTabs = ({
        tabWidths = widths,
        start,
        end,
        actionButtonsCount = 2,
    }) => {
        if (!tabsContainerRef.current) return 1;
        if (!tabWidths?.length) return 1;

        const containerWidth = tabsContainerRef.current.offsetWidth;

        const allTabsWidth = tabWidths.reduce((partialSum, a) => partialSum + Math.ceil(a), 0);
        const allTabsVisible = allTabsWidth <= containerWidth;

        if (allTabsVisible) return tabWidths.length || 1;

        const availableWidth = containerWidth - actionButtonsCount * buttonWidth;

        let count = 0;
        let restWidth = availableWidth;

        if (typeof end === 'number') {
            for (let i = end; i >= 0; i -= 1) {
                const tabWidth = tabWidths[i];

                if (tabWidth <= restWidth) {
                    count += 1;
                    restWidth = restWidth - tabWidth;
                } else if (tabWidth > restWidth) {
                    break;
                }
            }
        } else if (typeof start === 'number') {
            for (let i = start; i < tabWidths.length; i++) {
                const tabWidth = tabWidths[i];

                if (tabWidth <= restWidth) {
                    count += 1;
                    restWidth = restWidth - tabWidth;
                } else if (tabWidth > restWidth) {
                    break;
                }
            }
        }

        return Math.max(count, 1);
    };

    const handleMoreClick = () => {
        const prevEndIndex = visibleStartIndex + visibleTabsCount - 1;

        const lastTabIndex = tabs.length - 1;
        const nextEndIndex = Math.min(prevEndIndex + 1, lastTabIndex);

        const actionButtonsCount = nextEndIndex === lastTabIndex ? 1 : 2;
        let count = calculateCountOfVisibleTabs({ end: nextEndIndex, actionButtonsCount });
        let nextStartIndex = Math.max(0, nextEndIndex - count + 1);
        if (nextStartIndex <= visibleStartIndex && tabs.length - 1 > nextEndIndex) {
            count = calculateCountOfVisibleTabs({ start: nextStartIndex + 1 });
            nextStartIndex += 1;
        }

        setVisibleTabsCount(count);
        setVisibleStartIndex(nextStartIndex);
    };

    const handlePreviousClick = () => {
        const prevStartIndex = Math.max(visibleStartIndex - 1, 0);

        const actionButtonsCount = prevStartIndex === 0 ? 1 : 2;
        const count = calculateCountOfVisibleTabs({ start: prevStartIndex, actionButtonsCount });

        setVisibleTabsCount(count);
        setVisibleStartIndex(prevStartIndex);
    };

    const updateVisibleTabs = () => {
        const tabWidths = tabRefs.current.map((el) => el?.offsetWidth || 0);
        setWidths(tabWidths);

        setVisibleStartIndex(0);

        const count = calculateCountOfVisibleTabs({
            tabWidths,
            start: 0,
            actionButtonsCount: 1,
        });

        setVisibleTabsCount(count);
    };

    const debouncedUpdate = useRef(debounce(updateVisibleTabs, 100)).current;

    useEffect(() => {
        // on page load
        updateVisibleTabs();
    }, []);

    useEffect(() => {
        // on screen size change
        debouncedUpdate();
    }, [screenWidth]);

    const visibleTabs = tabs.slice(visibleStartIndex, visibleStartIndex + visibleTabsCount);

    return (
        <div className={pageHorizontalPaddings}>
            <div
                ref={tabsContainerRef}
                className={classNames('overflow-x-auto', containerClassName)}
            >
                <div className="flex items-center">
                    {withPrevButton && (
                        <button
                            onClick={handlePreviousClick}
                            className={classNames(
                                buttonClassName,
                                'flex items-center justify-center min-h-[46px] rounded-l-[10px]'
                            )}
                        >
                            <ArrowLeftSLineIcon width={18} height={18} color="#33373D" />
                        </button>
                    )}

                    <div className="flex">
                        {visibleTabs.map((tab, index) => {
                            const isSelected = activeTab.order === tab.order;

                            let className = '';
                            if (index === 0 && !withPrevButton) {
                                className = 'rounded-l-[10px] ';
                            }

                            if (index === visibleTabs.length - 1 && !withNextButton) {
                                className = className + 'rounded-r-[10px]';
                            }

                            return (
                                <TabButton
                                    tab={tab}
                                    key={`${tab.type}-${tab.order}`}
                                    isSelected={isSelected}
                                    className={className}
                                    onClick={() => setActiveTab(tab)}
                                />
                            );
                        })}
                    </div>

                    {withNextButton && (
                        <button
                            onClick={handleMoreClick}
                            className={classNames(
                                buttonClassName,
                                'flex items-center justify-center min-h-[46px] rounded-r-[10px]'
                            )}
                        >
                            <ArrowRightSLineIcon width={18} height={18} color="#33373D" />
                        </button>
                    )}
                </div>

                <div
                    className="absolute -top-[9999px] left-0 opacity-0 pointer-events-none"
                    style={{ visibility: 'hidden' }}
                >
                    <div className="flex bg-gray-50 rounded-lg p-1">
                        {tabs.map((tab, index) => (
                            <TabButton
                                key={`measure-${tab.type}-${tab.order}`}
                                ref={(el) => (tabRefs.current[index] = el)}
                                tab={tab}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TabButton = forwardRef(
    ({ tab, isSelected = false, handleTabClick = () => {}, className, ...props }, ref) => {
        return (
            <div ref={ref} className="relative z-10 flex-shrink-0">
                <button
                    onClick={handleTabClick}
                    className={classNames(
                        'flex-shrink-0 transition-colors',
                        buttonClassName,
                        className,
                        'font-body text-body-regular-m bg-white text-neutral-400',
                        isSelected && 'opacity-0'
                    )}
                    {...props}
                >
                    {tab.label}
                </button>

                {isSelected && (
                    <div
                        className={classNames(
                            'absolute top-0 bottom-0 left-[-1px] right-[-1px] flex items-center justify-center font-body-bold text-body-bold-m bg-gray-900 text-white cursor-pointer',
                            'border-1 border-neutral-400',
                            'animate-fadeIn',
                            className
                        )}
                    >
                        {tab.label}
                    </div>
                )}
            </div>
        );
    }
);

export default TabNavigation;
